import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css'; // Import Quill styles

const EditAboutPage = ({ token }) => {
    const apiBaseUrl = import.meta.env.VITE_API_URL;

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [headerImageUrl, setHeaderImageUrl] = useState(''); // Stores the URL of the current/newly uploaded image
    const [selectedFile, setSelectedFile] = useState(null); // Stores the file object if a new one is chosen
    const [imagePreview, setImagePreview] = useState(''); // For displaying the image preview
    
    const [formMessage, setFormMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    const fetchAboutContent = useCallback(async () => {
        if (!token) return;
        setIsLoading(true);
        setFormMessage('');
        try {
            const response = await fetch(`${apiBaseUrl}/api/about-content`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                if (response.status === 404) {
                    // If 404, it means no content has been set yet. This is not an error for the form.
                    // Initialize with default empty/placeholder values.
                    setTitle('About Us'); // Default title
                    setContent('');
                    setHeaderImageUrl('');
                    setImagePreview('');
                    setFormMessage('No existing content found. Please enter new content.');
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            } else {
                const data = await response.json();
                setTitle(data.title || 'About Us');
                setContent(data.content || '');
                setHeaderImageUrl(data.header_image_url || '');
                if (data.header_image_url) {
                    try {
                        const fullImageUrl = new URL(data.header_image_url, apiBaseUrl.startsWith('http') ? apiBaseUrl : window.location.origin).href;
                        setImagePreview(fullImageUrl);
                    } catch (e) {
                        console.error("Error constructing image preview URL from fetched data:", e);
                        setImagePreview(''); 
                    }
                } else {
                    setImagePreview('');
                }
            }
        } catch (err) {
            console.error("Error fetching About Us content:", err);
            setFormMessage(`Error fetching content: ${err.message}`);
        } finally {
            setIsLoading(false);
            setInitialDataLoaded(true);
        }
    }, [token, apiBaseUrl]);

    useEffect(() => {
        fetchAboutContent();
    }, [fetchAboutContent]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setImagePreview(URL.createObjectURL(file)); // Show preview of selected file
            setHeaderImageUrl(''); // Clear existing image URL if new file is chosen
        } else {
            // If file selection is cancelled, revert to original image if available
            setSelectedFile(null);
            if (headerImageUrl) { // This would be the one from initial load
                 try {
                    const fullImageUrl = new URL(headerImageUrl, apiBaseUrl.startsWith('http') ? apiBaseUrl : window.location.origin).href;
                    setImagePreview(fullImageUrl);
                } catch (e) {
                    console.error("Error constructing image preview URL on file cancel:", e);
                    setImagePreview(''); 
                }
            } else {
                setImagePreview('');
            }
        }
    };

    const handleRemoveImage = () => {
        setSelectedFile(null);
        setImagePreview('');
        setHeaderImageUrl(''); // Explicitly set to empty to indicate removal
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!initialDataLoaded) {
            setFormMessage('Please wait for initial content to load.');
            return;
        }
        setIsLoading(true);
        setFormMessage('');

        const formData = new FormData();
        formData.append('title', title);
        formData.append('content', content);

        if (selectedFile) {
            formData.append('headerImageFile', selectedFile);
        } else {
            // If no new file is selected, send the current headerImageUrl 
            // (which might be an existing URL, or an empty string if user removed it)
            formData.append('header_image_url', headerImageUrl);
        }

        try {
            const response = await fetch(`${apiBaseUrl}/api/about-content`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // 'Content-Type': 'multipart/form-data' is automatically set by browser with FormData
                },
                body: formData,
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || 'Failed to update About Us content');
            }
            
            setFormMessage('About Us content updated successfully!');
            // Update state with returned data to reflect changes, especially new image URL if uploaded
            if (result.updatedData) {
                setHeaderImageUrl(result.updatedData.header_image_url || '');
                if (result.updatedData.header_image_url) {
                     try {
                        const fullImageUrl = new URL(result.updatedData.header_image_url, apiBaseUrl.startsWith('http') ? apiBaseUrl : window.location.origin).href;
                        setImagePreview(fullImageUrl);
                    } catch (e) {
                        console.error("Error constructing image preview URL from update response:", e);
                        setImagePreview(''); 
                    }
                } else {
                    setImagePreview('');
                }
                setSelectedFile(null); // Clear selected file after successful upload
            }

        } catch (err) {
            console.error('Error updating About Us content:', err);
            setFormMessage(`Error: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
            [{size: []}],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{'list': 'ordered'}, {'list': 'bullet'}, 
             {'indent': '-1'}, {'indent': '+1'}],
            ['link', 'image', 'video'], // 'image' in Quill toolbar typically handles base64, not server uploads directly
            ['clean'],
            [{ 'color': [] }, { 'background': [] }], // Color and background color
            [{ 'align': [] }], // Text alignment
        ],
    };

    const formStyle = {
        maxWidth: '800px',
        margin: '20px auto',
        padding: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)'
    };

    const inputGroupStyle = {
        marginBottom: '20px'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '8px',
        fontWeight: 'bold'
    };

    const inputStyle = {
        width: '100%',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxSizing: 'border-box'
    };

    const buttonStyle = {
        padding: '10px 20px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '16px'
    };

    const imagePreviewStyle = {
        marginTop: '10px',
        maxWidth: '100%',
        maxHeight: '300px',
        border: '1px solid #ddd',
        padding: '5px'
    };

    return (
        <div style={formStyle}>
            <h2>Edit About Us Page Content</h2>
            <form onSubmit={handleSubmit}>
                <div style={inputGroupStyle}>
                    <label htmlFor="aboutTitle" style={labelStyle}>Title:</label>
                    <input 
                        type="text" 
                        id="aboutTitle" 
                        value={title} 
                        onChange={(e) => setTitle(e.target.value)} 
                        style={inputStyle}
                        disabled={isLoading}
                    />
                </div>

                <div style={inputGroupStyle}>
                    <label htmlFor="aboutContent" style={labelStyle}>Content:</label>
                    <ReactQuill 
                        theme="snow" 
                        value={content} 
                        onChange={setContent} 
                        modules={quillModules}
                        style={{ backgroundColor: 'white', color: 'black' }} // Ensure editor is visible
                        readOnly={isLoading}
                    />
                </div>

                <div style={inputGroupStyle}>
                    <label htmlFor="headerImage" style={labelStyle}>Header Image:</label>
                    <input 
                        type="file" 
                        id="headerImage" 
                        accept="image/*" 
                        onChange={handleFileChange} 
                        style={{...inputStyle, padding: '10px 0'}}
                        disabled={isLoading}
                    />
                    {imagePreview && (
                        <div>
                            <img src={imagePreview} alt="Header Preview" style={imagePreviewStyle} />
                            <button type="button" onClick={handleRemoveImage} style={{...buttonStyle, backgroundColor: '#dc3545', marginTop: '10px'}} disabled={isLoading}>
                                Remove Image
                            </button>
                        </div>
                    )}
                </div>
                
                <button type="submit" style={buttonStyle} disabled={isLoading || !initialDataLoaded}>
                    {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
                
                {formMessage && <p style={{ marginTop: '15px', color: formMessage.startsWith('Error') ? 'red' : 'green' }}>{formMessage}</p>}
            </form>
        </div>
    );
};

export default EditAboutPage;
